/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement, useCallback, useEffect, useState } from "react"
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputBase,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import {
  PLUGIN_CATALOG,
  PLUGIN_DEFINITIONS,
  PLUGIN_IDS,
  PluginId,
} from "@medaica/common/components/pcg-viewer/plugins/plugin-definitions"
import { pluginResult } from "@medaica/common/components/pcg-viewer/plugins/vendor-results"
import type {
  PcgPluginReview,
  PcgPluginReviews,
  PcgPluginReviewStatus,
  PcgVendorEvidence,
} from "@medaica/common/services/medaica-api-service/pcg-review-types"

/**
 * Plugins rack — the handoff's 3rd-Party Evidence rack (handoff v0.8.3
 * `.aptm-tools` markup + the plugin script block, styled by
 * pcg-exam-workspace.css): ACT/BeatAware modules with a power switch, a
 * review LED, a 5-section detail panel (state / configuration / connection
 * contract / result / clinician review), and an inert marketplace catalog.
 * The Result section shows the selected recording's stored ACT/BeatAware
 * results (`vendor-results.ts`) where the handoff shows fixed fixture rows.
 * The reviewer's Accept / Reject / Note is saved with the recording's
 * disposition (`pluginReviews`) and reaches the Medaica Report's Evidence
 * panels through the review snapshot; it never changes the recording's
 * decision or what feeds the report.
 *
 * Handoff behaviors kept exactly (see plugins/README.md for the list of
 * known handoff↔spec divergences that parity preserves):
 * - Enablement is per-plugin, default on, sessionStorage-backed.
 * - Review state {status, note} is per recording and plugin.
 * - Accept/Reject toggle off to unreviewed; a note flips status to noted;
 *   saving a blank note reverts to unreviewed; the note text survives a later
 *   Accept/Reject.
 * - LED: grey disabled/none → white "available" when enabled → green/red/blue
 *   for accepted/rejected/noted.
 * Deliberate deviations: with no ready result for the recording the LED stays
 * the bare grey ring and the review buttons are disabled (the handoff has no
 * no-result state; the third-party evidence spec §6.4 keeps result
 * availability apart from disposition); the review state is saved on the
 * server instead of in sessionStorage; the handoff's "no live vendor
 * request" note is dropped; MUI Dialog replaces the hand-rolled backdrop
 * (Escape/backdrop-close come free); the note editor is inline like
 * DispositionControls (window.prompt not reproduced); the rack shows at all
 * widths (the portal sidebar has no <900px hide rule).
 */

type PluginReviewStatus = PcgPluginReviewStatus
interface PluginReview {
  status: PluginReviewStatus
  note: string
}

const UNREVIEWED: PluginReview = { status: "unreviewed", note: "" }

// The enabled map is a viewer preference, version-scoped (shared across
// exams) as in the handoff.
const PLUGIN_STATE_KEY = "pcg-review:plugins"

const readEnabledMap = (): { [id in PluginId]?: boolean } => {
  try {
    return JSON.parse(sessionStorage.getItem(PLUGIN_STATE_KEY) ?? "{}") as { [id in PluginId]?: boolean }
  } catch {
    return {}
  }
}

const REVIEW_LABELS: { [status in PluginReviewStatus]: string } = {
  unreviewed: "Unreviewed",
  accepted: "Accepted",
  rejected: "Rejected",
  noted: "Note added; unreviewed",
}

// LED class per review state: white "available" when enabled and unreviewed,
// then green/red/blue; a disabled plugin shows the bare grey ring.
const LED_CLASSES: { [status in PluginReviewStatus]: string } = {
  unreviewed: "available",
  accepted: "accepted",
  rejected: "rejected",
  noted: "noted",
}

const PluginRack = ({
  recordingId,
  recordingLabel,
  vendorEvidence,
  pluginReviews,
  onReviewChange,
  busy = false,
}: {
  recordingId: string | null
  recordingLabel: string
  /** The selected recording's stored vendor results. */
  vendorEvidence: PcgVendorEvidence | null | undefined
  /** The selected recording's saved plugin reviews. */
  pluginReviews: PcgPluginReviews | null | undefined
  /** Saves one plugin's review for the selected recording. */
  onReviewChange: (id: PluginId, review: PcgPluginReview) => void
  /** A save is in flight: the review buttons wait. */
  busy?: boolean
}): ReactElement => {
  const [enabledMap, setEnabledMap] = useState<{ [id in PluginId]?: boolean }>(readEnabledMap)
  const [openPanel, setOpenPanel] = useState<PluginId | "catalog" | null>(null)
  const [noteEditorOpen, setNoteEditorOpen] = useState(false)
  const [noteDraft, setNoteDraft] = useState("")

  // Recording switches close the panel and reset the note editor — the
  // handoff achieved the same via full page navigation per recording.
  useEffect(() => {
    setOpenPanel(null)
    setNoteEditorOpen(false)
  }, [recordingId])

  const pluginEnabled = useCallback((id: PluginId): boolean => enabledMap[id] !== false, [enabledMap])

  const review = useCallback(
    (id: PluginId): PluginReview => {
      const saved = recordingId ? pluginReviews?.[id] : null
      return saved ? { status: saved.status, note: saved.note ?? "" } : UNREVIEWED
    },
    [recordingId, pluginReviews]
  )

  const setPluginEnabled = (id: PluginId, enabled: boolean) => {
    const next = { ...enabledMap, [id]: enabled }
    setEnabledMap(next)
    sessionStorage.setItem(PLUGIN_STATE_KEY, JSON.stringify(next))
  }

  // Handoff setPluginReview: re-clicking the active status (except noted)
  // reverts to unreviewed; the note text survives a later Accept/Reject.
  const setPluginReview = (id: PluginId, status: PluginReviewStatus, note?: string) => {
    if (!recordingId) {
      return
    }
    const current = review(id)
    const nextStatus = current.status === status && status !== "noted" ? "unreviewed" : status
    const nextNote = note ?? current.note
    onReviewChange(id, { status: nextStatus, note: nextNote || null })
  }

  const openPlugin = openPanel && openPanel !== "catalog" ? PLUGIN_DEFINITIONS[openPanel] : null
  const openReview = openPanel && openPanel !== "catalog" ? review(openPanel) : UNREVIEWED
  const openResult = openPanel && openPanel !== "catalog" ? pluginResult(openPanel, vendorEvidence) : null
  // Review needs a ready result; with none, the review buttons are disabled.
  const reviewable = (openResult?.available ?? false) && !busy

  const saveNote = () => {
    if (openPanel && openPanel !== "catalog") {
      const trimmed = noteDraft.trim()
      setPluginReview(openPanel, trimmed ? "noted" : "unreviewed", trimmed)
    }
    setNoteEditorOpen(false)
  }

  const panelRow = (label: string, value: string) => (
    <Box display="flex" justifyContent="space-between" gap={2} py={0.25}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} textAlign="right">
        {value}
      </Typography>
    </Box>
  )

  return (
    <div className="aptm-tools" aria-label="Evidence plugins">
      <div className="aptm-tools-title">Plugins</div>
      {PLUGIN_IDS.map((id) => {
        const plugin = PLUGIN_DEFINITIONS[id]
        const enabled = pluginEnabled(id)
        const reviewState = review(id)
        const reviewLabel = REVIEW_LABELS[reviewState.status]
        const hasResult = pluginResult(id, vendorEvidence).available
        const showsResult = enabled && hasResult
        // Row tooltip ending and LED label: a result to review, none for this
        // recording, or the plugin switched off.
        let resultTitle = "No result displayed"
        let ledLabel = "No result displayed; plugin disabled"
        if (showsResult) {
          resultTitle = `Result ${reviewLabel}`
          ledLabel = `Plugin result ${reviewLabel.toLowerCase()}`
        } else if (enabled) {
          resultTitle = "No result available"
          ledLabel = "No result available for this recording"
        }
        const rowClasses = ["aptm-plugin-btn", openPanel === id ? "open" : "", enabled ? "" : "disabled"]
          .filter(Boolean)
          .join(" ")
        return (
          <button
            key={id}
            type="button"
            className={rowClasses}
            data-plugin-id={id}
            onClick={() => {
              setNoteEditorOpen(false)
              setOpenPanel(id)
            }}
            title={`${plugin.name} · ${enabled ? "Enabled" : "Disabled"} · ${plugin.scope} · ` + resultTitle}
          >
            {/* The power glyph toggles enablement without opening the panel
                (the handoff nests a power button inside the row button). */}
            <span
              className="plugin-power"
              role="switch"
              tabIndex={0}
              aria-checked={enabled}
              aria-label={enabled ? "Plugin enabled" : "Plugin disabled"}
              title="Turn plugin on or off"
              onClick={(event) => {
                event.stopPropagation()
                setPluginEnabled(id, !enabled)
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault()
                  event.stopPropagation()
                  setPluginEnabled(id, !enabled)
                }
              }}
            >
              ⏻
            </span>
            <span>{plugin.name}</span>
            <span
              className={showsResult ? `plugin-result-led ${LED_CLASSES[reviewState.status]}` : "plugin-result-led"}
              aria-label={ledLabel}
            />
          </button>
        )
      })}
      {[0, 1].map((slot) => (
        <button
          key={slot}
          type="button"
          className="aptm-plugin-slot"
          data-add-plugin
          onClick={() => setOpenPanel("catalog")}
        >
          ＋ Add plugin
        </button>
      ))}

      {/* Plugin detail panel — the handoff's 5 sections */}
      <Dialog open={openPlugin !== null} onClose={() => setOpenPanel(null)} maxWidth="xs" fullWidth>
        {openPlugin && openPanel !== "catalog" && (
          <>
            <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
              {openPlugin.name}
              <IconButton aria-label="Close plugin panel" size="small" onClick={() => setOpenPanel(null)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="overline" color="text.secondary" display="block">
                    Plugin State
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {pluginEnabled(openPanel as PluginId) ? "Enabled" : "Disabled"}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setPluginEnabled(openPanel as PluginId, !pluginEnabled(openPanel as PluginId))}
                >
                  {pluginEnabled(openPanel as PluginId) ? "Turn off" : "Turn on"}
                </Button>
              </Box>
              <Box mb={2}>
                <Typography variant="overline" color="text.secondary">
                  Configuration
                </Typography>
                {panelRow("Type", openPlugin.type)}
                {panelRow("Scope", `${openPlugin.scope} · ${recordingLabel}`)}
                {panelRow("Input", openPlugin.input)}
              </Box>
              <Box mb={2}>
                <Typography variant="overline" color="text.secondary">
                  Connection Contract
                </Typography>
                {panelRow("API adapter", openPlugin.connection)}
                {panelRow("Expected output", openPlugin.output)}
              </Box>
              <Box mb={2}>
                <Typography variant="overline" color="text.secondary">
                  Result
                </Typography>
                {openResult?.rows.map(([label, value]) => (
                  <React.Fragment key={label}>{panelRow(label, value)}</React.Fragment>
                ))}
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary" display="block">
                  Clinician Review · {openReview.status === "noted" ? "Noted" : REVIEW_LABELS[openReview.status]}
                </Typography>
                <Stack direction="row" spacing={1} mt={0.5}>
                  <Button
                    size="small"
                    color="success"
                    variant={openReview.status === "accepted" ? "contained" : "outlined"}
                    disabled={!reviewable}
                    onClick={() => setPluginReview(openPanel as PluginId, "accepted")}
                  >
                    Accept supporting evidence
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    variant={openReview.status === "rejected" ? "contained" : "outlined"}
                    disabled={!reviewable}
                    onClick={() => setPluginReview(openPanel as PluginId, "rejected")}
                  >
                    Reject
                  </Button>
                  <Button
                    size="small"
                    color="info"
                    variant={openReview.status === "noted" ? "contained" : "outlined"}
                    disabled={!reviewable}
                    onClick={() => {
                      setNoteDraft(openReview.note)
                      setNoteEditorOpen((open) => !open)
                    }}
                  >
                    Note
                  </Button>
                </Stack>
                <Collapse in={noteEditorOpen}>
                  <Box mt={1.5}>
                    <TextField
                      label={`Reviewer note · ${openPlugin.name} · ${recordingLabel}`}
                      multiline
                      minRows={2}
                      fullWidth
                      size="small"
                      value={noteDraft}
                      onChange={(event) => setNoteDraft(event.target.value)}
                    />
                    <Stack direction="row" spacing={1} mt={1}>
                      <Button size="small" variant="contained" onClick={saveNote}>
                        Save note
                      </Button>
                      <Button size="small" onClick={() => setNoteEditorOpen(false)}>
                        Cancel
                      </Button>
                    </Stack>
                  </Box>
                </Collapse>
                {!noteEditorOpen && openReview.note && (
                  <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                    Reviewer note: {openReview.note}
                  </Typography>
                )}
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* "Add Plugin" marketplace preview — inert, as in the handoff */}
      <Dialog open={openPanel === "catalog"} onClose={() => setOpenPanel(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          Add Plugin
          <IconButton aria-label="Close plugin panel" size="small" onClick={() => setOpenPanel(null)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            Marketplace preview · Browsing and installation are outside the current MVP.
          </Typography>
          <InputBase
            type="search"
            placeholder="Search available plugins…"
            inputProps={{ "aria-label": "Search available plugins" }}
            fullWidth
            autoFocus
            sx={{ border: 1, borderColor: "divider", borderRadius: 1, px: 1, py: 0.25, mb: 1.5, fontSize: 14 }}
          />
          {PLUGIN_CATALOG.map(({ section, items }) => (
            <Box key={section} mb={1.5}>
              <Typography variant="overline" color="text.secondary">
                {section}
              </Typography>
              {items.map((item) => (
                <Box key={item.name} display="flex" justifyContent="space-between" alignItems="center" py={0.5}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {item.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.description}
                    </Typography>
                  </Box>
                  <Chip label={item.state} size="small" variant="outlined" />
                </Box>
              ))}
            </Box>
          ))}
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default PluginRack
export { PLUGIN_STATE_KEY }
